import { useCallback } from "react";
import { useFormContext } from "react-hook-form";
import { useIntl } from "react-intl";

import { useGetCountries } from "api/queries/common/useGetCountries";
import { selectedFreightMode, selectedProvider } from "helpers/freightModeHelper";
import { useGetCommonFreightModes } from "api/queries/common/useGetCommonFreightModes";
import errorToast from "helpers/errorToast";
import { ICountryOption } from "types/Common";

interface UseHeaderDataParams {
  isOffPlatformShipment?: boolean;
  state: any;
}

const PROVIDERS_DISPLAY_LIMIT = 3;

function getPaginatedData(array: any, offset: number, limit: number) {
  if (array?.length) {
    if (limit < 0) return array.slice(offset);
    return array.slice(offset, limit);
  }
}

function useHeaderData({ isOffPlatformShipment, state }: UseHeaderDataParams) {
  const { getValues, watch } = useFormContext();
  const intl = useIntl();

  const isPickupReady = watch("schedule.pickup_cargo_ready");
  const isDeliveryReady = watch("schedule.delivery_cargo_ready");

  const { data: unmappedFreightModes = [] } = useGetCommonFreightModes({
    options: {
      enabled: isOffPlatformShipment,
      onError: (err) =>
        errorToast(
          err,
          intl.formatMessage({
            id: "general.couldNotRetrieveFreightModes",
            defaultMessage: "Could not retrieve freight modes. Please try again later.",
          }),
        ),
    },
  });

  const { data: countries } = useGetCountries({
    refetchOnMount: false,
  });

  const freightModes: any = unmappedFreightModes?.map((freight: any) => ({
    label: freight.value,
    value: freight.key,
  }));

  const offPlatformFreightMode = freightModes.find((freight: any) => freight.value === watch("shipment.freight_mode"));

  const freightMode = !isOffPlatformShipment
    ? selectedFreightMode(state.freightModes.available_modes, watch("invited_modes"))
    : offPlatformFreightMode
    ? [offPlatformFreightMode]
    : [];

  const offPlatformProvider = (state?.providers?.[0] ?? []).find(
    (provider: any) => provider.value === watch("shipment.provider"),
  );

  const providers = selectedProvider(state.providers.invited_providers, watch("invited_providers")?.providers);

  const incotermOptions: any = Object.values(state?.service?.incoterm_values ?? {});
  const incotermValue = watch("service.incoterms");
  const incoterm =
    incotermValue && incotermOptions?.length > 0
      ? incotermOptions?.find((option: any) => option.value === getValues("service.incoterms"))?.label
      : "";

  const fromCountry = useCallback(
    () =>
      countries?.find(
        (country: ICountryOption) =>
          country.value === getValues("schedule.pickup_country") || country.label === getValues("schedule.pickup_country"),
      ),
    [countries, watch("schedule.pickup_country"), watch("schedule.pickup_address_type")],
  );

  const toCountry = useCallback(
    () =>
      countries?.find(
        (country: ICountryOption) =>
          country.value === getValues("schedule.stop1_country") || country.label === getValues("schedule.stop1_country"),
      ),
    [countries, watch("schedule.stop1_country"), watch("schedule.stop1_address_type")],
  );

  const packingList =
    getValues("packing_list")?.length > 0 &&
    getValues("packing_list").map((item: any) => ({
      units: item.units,
      weight: item.weight_piece,
      w_unit: item.w_unit,
    }));

  const total: () => { units: number; weight: number } = useCallback(
    () =>
      packingList &&
      packingList.reduce(
        (acc: any, curr: any) => {
          acc.units += Number(curr.units);
          if (curr.w_unit && curr.w_unit.toLowerCase().includes("total")) {
            acc.weight += Number(curr.weight);
          } else {
            acc.weight += Number(curr.weight) * Number(curr.units);
          }
          return acc;
        },
        { units: 0, weight: 0 },
      ),
    [packingList],
  );

  const providersList = getPaginatedData(providers, 0, PROVIDERS_DISPLAY_LIMIT) || [];

  return {
    getValues,
    isPickupReady,
    isDeliveryReady,
    freightMode,
    offPlatformProvider,
    providers,
    providersList,
    incoterm,
    fromCountry,
    toCountry,
    total,
    providersDisplayLimit: PROVIDERS_DISPLAY_LIMIT,
  };
}

export default useHeaderData;
